// Host Home — "/" — 3-column dashboard for signed-in hosts.
// Logged-out state preserves the minimalist hero with bottom sign-in pill.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Link2, Copy, Plus, Calendar, ImageIcon, Video, Users,
  Image as ImageLucide, Share2, MoreHorizontal, Zap, Bell, ChevronRight,
  Trash2, MessageCircleMore, Mail, ChevronDown, Activity as ActivityIcon, BarChart3,
  Camera, Lock, Eye, EyeOff, Pencil, Check, X as XClose,
} from "lucide-react";
import PageShell from "../components/PageShell";
import ShareEventModal from "../components/ShareEventModal";
import { buildInviteText } from "../lib/shareInvite";
import MeyecamLogo from "../components/MeyecamLogo";
import HostHeaderLogo from "../components/HostHeaderLogo";
import { Slogan } from "../components/Slogan";
import { Albums, Host, Media, Joined, uploadFiles, getHostPasscodePlain, setHostPasscodePlain } from "../lib/api";
import { slugify } from "../lib/slugify";
import { EVENT_ICONS, getIconConfig } from "../lib/eventIcons";
import { useAuth } from "../lib/AuthContext";
import { timeAgo, formatDate } from "../lib/time";

export default function HostHome() {
  const { user, loading } = useAuth();

  if (!user) {
    return (
      <PageShell>
        <div className="flex flex-col items-center" style={{ paddingTop: 12 }}>
          <MeyecamLogo size={260} className="lg:!w-[300px] lg:!h-[300px]" />
          <Slogan size="lg" className="mt-2" />
        </div>
        <LoggedOutHero loading={loading} />
      </PageShell>
    );
  }

  return <HostDashboard user={user} />;
}

// ───────────────────────────────────── Logged-in dashboard ─────────────────────────────────────

function HostDashboard({ user }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [joined, setJoined] = useState([]);
  const [stats, setStats] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const refresh = async () => {
    try {
      const [list, s, j] = await Promise.all([
        Albums.list(),
        Host.stats().catch(() => null),
        Joined.list().catch(() => []),
      ]);
      setAlbums(list);
      setJoined(j);
      if (s) setStats(s);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <PageShell
      dashboard
      headerLeft={<HostHeaderLogo />}
      headerCenter={<Slogan size="md" className="hidden md:flex" />}
      headerRight={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/activity")}
            className="relative w-11 h-11 rounded-full flex items-center justify-center transition"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
            data-testid="header-bell"
            title="Activity"
          >
            <Bell size={18} />
            {stats?.recent_activity?.length > 0 && (
              <span
                className="absolute"
                style={{
                  top: 8, right: 9, width: 8, height: 8, borderRadius: 999,
                  background: "#22E2FF", boxShadow: "0 0 8px rgba(34,226,255,0.7)",
                }}
              />
            )}
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-full px-2.5 py-1.5 pr-3 transition"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="header-profile"
            >
              {user.picture ? (
                <img
                  src={user.picture} alt="" referrerPolicy="no-referrer"
                  className="rounded-full object-cover"
                  style={{ width: 30, height: 30 }}
                />
              ) : (
                <div className="rounded-full" style={{ width: 30, height: 30, background: "linear-gradient(135deg,#FF7A00,#FF1F6B)" }} />
              )}
              <span className="text-sm font-medium">{(user.name || user.email || "").split(" ")[0]}</span>
              <ChevronDown size={14} className="opacity-60" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 rounded-2xl overflow-hidden"
                style={{
                  minWidth: 180,
                  zIndex: 30,
                  background: "#15131F",
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: "0 20px 60px -10px rgba(0,0,0,0.8)",
                }}
              >
                <button
                  onClick={() => { setMenuOpen(false); signOut(); }}
                  className="w-full text-left px-4 py-3 text-sm hover:bg-white/5"
                  data-testid="signout-btn"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      }
    >
      {/* Mobile slogan (header center is hidden on small screens) */}
      <div className="md:hidden mb-4 flex justify-center">
        <Slogan size="sm" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,360px)_minmax(0,1fr)_minmax(0,340px)] gap-5">
        <CreateEventCard onCreated={refresh} />
        <div className="flex flex-col gap-5 min-w-0">
          <YourEvents albums={albums} onChanged={refresh} />
          {joined.length > 0 && <JoinedEvents albums={joined} onChanged={refresh} />}
        </div>
        <RightRail stats={stats} />
      </div>

      <div
        className="mt-8 mx-auto max-w-2xl text-center text-sm flex items-center justify-center gap-2"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        <span style={{ fontSize: 16 }}>💡</span>
        <span>
          <span style={{ color: "#FFD300", fontWeight: 600 }}>Pro Tip:</span> Guests can upload photos and videos without creating an account.
        </span>
      </div>
    </PageShell>
  );
}

// ───────────────────────────────────── Create event (left) ─────────────────────────────────────

function CreateEventCard({ onCreated }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [hostName, setHostName] = useState("");
  const [icon, setIcon] = useState("wedding");
  const [wantPasscode, setWantPasscode] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const slug = useMemo(() => slugify(name), [name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !hostName.trim()) {
      toast.error("Please fill event name and your name");
      return;
    }
    if (!slug) { toast.error("Event name must contain letters or numbers"); return; }
    const pass = wantPasscode ? passcode.trim() : "";
    if (wantPasscode && !pass) { toast.error("Enter a passcode or turn off protection"); return; }
    setSubmitting(true);
    try {
      const body = { slug, name: name.trim(), host_name: hostName.trim(), icon };
      if (pass) body.passcode = pass;
      const created = await Albums.create(body);
      if (pass) setHostPasscodePlain(created.slug, pass); // remember for Share text
      toast.success("Event created!");
      onCreated?.();
      navigate(`/${created.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="glass-card p-6"
      data-testid="create-event-card"
      style={{ borderColor: "rgba(127,75,255,0.35)", boxShadow: "0 0 0 1px rgba(127,75,255,0.18) inset" }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.5)" }}
        >
          <Plus size={20} color="#9D4EDD" />
        </div>
        <div>
          <div className="text-lg font-semibold tracking-tight">Create New Event</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            Create a shareable album for your special moments.
          </div>
        </div>
      </div>

      <label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: "rgba(255,255,255,0.65)" }}>
        Event name
      </label>
      <input
        className="meye-input mb-5"
        placeholder="Event name (e.g. Gurpreet's Wedding)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="event-name-input"
        autoFocus
        maxLength={80}
      />

      <label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: "rgba(255,255,255,0.65)" }}>
        Shareable link
      </label>
      <div className="url-preview mb-5" data-testid="slug-preview">
        <Link2 size={16} style={{ color: "#7F4BFF" }} />
        <span className="truncate" style={{ color: "rgba(255,255,255,0.92)" }}>
          mEYEcam.com/<span style={{ color: "#fff", fontWeight: 600 }}>{slug || "your-event"}</span>
        </span>
        <button
          type="button"
          onClick={() => {
            if (!slug) return;
            navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
            toast.success("Link copied");
          }}
          className="ml-auto opacity-60 hover:opacity-100"
          aria-label="Copy link"
          data-testid="copy-preview-link"
        >
          <Copy size={14} />
        </button>
      </div>

      <label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: "rgba(255,255,255,0.65)" }}>
        Event type
      </label>
      <div className="grid grid-cols-2 gap-2 mb-5" data-testid="event-icons">
        {EVENT_ICONS.map((ev) => {
          const active = icon === ev.key;
          const Icon = ev.Icon;
          return (
            <button
              type="button"
              key={ev.key}
              className={`meye-chip ${active ? "active" : ""} justify-start`}
              onClick={() => setIcon(ev.key)}
              data-testid={`icon-chip-${ev.key}`}
              style={active ? {
                "--chip-active-bg": `${ev.color}22`,
                "--chip-active-border": ev.color,
              } : {}}
            >
              <Icon size={16} color={ev.color} />
              <span className="truncate">{ev.label}</span>
            </button>
          );
        })}
      </div>

      <label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: "rgba(255,255,255,0.65)" }}>
        Your name (host)
      </label>
      <input
        className="meye-input mb-5"
        placeholder="Your name (host)"
        value={hostName}
        onChange={(e) => setHostName(e.target.value)}
        data-testid="host-name-input"
        maxLength={60}
      />

      {/* Passcode toggle — private albums lock viewing + uploading */}
      <label
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 mb-3 cursor-pointer"
        style={{
          background: wantPasscode ? "rgba(255,211,0,0.10)" : "rgba(255,255,255,0.03)",
          border: `1px solid ${wantPasscode ? "rgba(255,211,0,0.45)" : "rgba(255,255,255,0.12)"}`,
        }}
        data-testid="create-passcode-toggle"
      >
        <input
          type="checkbox"
          checked={wantPasscode}
          onChange={(e) => setWantPasscode(e.target.checked)}
          className="accent-yellow-400"
          data-testid="create-passcode-checkbox"
        />
        <Lock size={13} color={wantPasscode ? "#FFD300" : "rgba(255,255,255,0.55)"} />
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-medium">Make this album private</div>
          <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.55)" }}>
            Only people with the passcode can view or upload
          </div>
        </div>
      </label>
      {wantPasscode && (
        <input
          type="text"
          className="meye-input mb-5"
          placeholder="Set a passcode (e.g. wedding2026)"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          maxLength={80}
          autoComplete="off"
          data-testid="create-passcode-input"
        />
      )}

      <button
        type="submit"
        className="cta-pill w-full"
        disabled={submitting}
        data-testid="create-event-btn"
      >
        {submitting ? <div className="meye-spinner" /> : "Create Event"}
      </button>
    </form>
  );
}

// ───────────────────────────────────── Events list (middle) ─────────────────────────────────────

function YourEvents({ albums, onChanged }) {
  const navigate = useNavigate();
  const [shareAlbum, setShareAlbum] = useState(null);

  // Copy = the full invite: message + link (+ passcode when locked).
  const copyInvite = (album) => {
    navigator.clipboard.writeText(buildInviteText(album));
    toast.success(album.requires_passcode ? "Invite copied — link + passcode included" : "Invite copied");
  };

  const onDelete = async (slug) => {
    if (!window.confirm("Delete this event and all its media?")) return;
    try {
      await Albums.remove(slug);
      toast.success("Event deleted");
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onChangeCover = async (album, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    const tid = toast.loading("Uploading new cover…");
    try {
      const res = await uploadFiles(album.slug, {
        uploaderName: album.host_name || "Host",
        files: [file],
      });
      if (!res?.uploaded) throw new Error("Upload failed");
      // Backend returns only a count; look up the newly-added media by filename.
      const list = await Media.list(album.slug);
      const match = [...list]
        .reverse()
        .find((m) => m.filename === file.name && !m.is_video);
      if (!match?.id) throw new Error("Couldn't locate uploaded image");
      await Albums.patch(album.slug, { cover_media_id: match.id });
      toast.success("Cover updated", { id: tid });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change cover", { id: tid });
    }
  };

  return (
    <section className="glass-card p-6" data-testid="your-events-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: "rgba(34,226,255,0.12)", border: "1px solid rgba(34,226,255,0.45)" }}
          >
            <Calendar size={20} color="#22E2FF" />
          </div>
          <div>
            <div className="text-lg font-semibold tracking-tight">Your Events</div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
              Manage your events and share memories.
            </div>
          </div>
        </div>
        <div className="text-sm font-medium flex items-center gap-1" style={{ color: "#22E2FF" }}>
          {albums.length} {albums.length === 1 ? "event" : "events"}
        </div>
      </div>

      {albums.length === 0 ? (
        <div
          className="rounded-2xl p-10 text-center"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
          data-testid="empty-events"
        >
          <Calendar size={32} className="mx-auto mb-3 opacity-50" />
          <div className="font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>No events yet</div>
          <div className="text-sm">Create your first event using the form on the left to get a shareable link.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="events-list">
          {albums.map((a) => (
            <EventRow
              key={a.id}
              album={a}
              // Hosts skip the guest landing — go straight to the gallery.
              onOpen={() => navigate(`/${a.slug}/gallery`)}
              onUpload={() => navigate(`/${a.slug}/upload`)}
              onCopy={() => copyInvite(a)}
              onShare={() => setShareAlbum(a)}
              onDelete={() => onDelete(a.slug)}
              onChangeCover={(file) => onChangeCover(a, file)}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      {shareAlbum && (
        <ShareEventModal
          album={shareAlbum}
          onClose={() => setShareAlbum(null)}
          onUpdated={(updated) => { setShareAlbum(updated); onChanged?.(); }}
        />
      )}
    </section>
  );
}

function EventRow({ album, onOpen, onUpload, onCopy, onShare, onDelete, onChangeCover, onChanged }) {
  const cfg = getIconConfig(album.icon);
  const Icon = cfg.Icon;
  const [menuOpen, setMenuOpen] = useState(false);
  const coverInputRef = useRef(null);

  const pickCover = () => coverInputRef.current?.click();
  const handleCoverFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (f) onChangeCover?.(f);
  };

  return (
    <div
      className="rounded-2xl grid grid-cols-1 sm:grid-cols-[160px_minmax(0,1fr)] relative"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        zIndex: menuOpen ? 30 : "auto",
      }}
      data-testid={`event-row-${album.slug}`}
    >
      {/* Cover */}
      <div
        className="relative overflow-hidden h-[180px] sm:h-full min-h-[160px] rounded-t-2xl sm:rounded-t-none sm:rounded-l-2xl group"
        style={{ background: `linear-gradient(135deg, ${cfg.color}22, ${cfg.color}05)` }}
      >
        <button
          type="button"
          onClick={onOpen}
          className="absolute inset-0 w-full h-full"
          title="Open album"
          aria-label="Open album"
        >
          {/* Always-rendered icon fallback (sits behind image, visible if image is missing/fails) */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: `${cfg.color}22`, border: `1px solid ${cfg.color}66` }}
            >
              <Icon size={28} color={cfg.color} />
            </div>
          </div>
          {album.cover_media_id && (
            <img
              src={Media.thumbUrl(album.cover_media_id)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </button>
        <div
          className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium pointer-events-none"
          style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}
        >
          <Icon size={11} color={cfg.color} />
          <span>{cfg.label}</span>
        </div>
        {/* Change cover — always visible on mobile, subtle until hover on desktop */}
        <button
          type="button"
          onClick={pickCover}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition sm:opacity-70 sm:group-hover:opacity-100 hover:!opacity-100"
          style={{ background: "rgba(0,0,0,0.65)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}
          data-testid={`change-cover-${album.slug}`}
          title="Change album cover"
        >
          <Camera size={12} />
          <span>Cover</span>
        </button>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleCoverFile}
          data-testid={`change-cover-input-${album.slug}`}
        />
      </div>

      {/* Details */}
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={onOpen}
              className="text-xl font-semibold tracking-tight truncate text-left hover:text-cyan-300 transition"
            >
              {album.name}
            </button>
            <div className="text-xs mt-1 flex items-center gap-2 flex-wrap" style={{ color: "rgba(255,255,255,0.55)" }}>
              <span>Created {formatDate(album.created_at)}</span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: "#22FFB7", boxShadow: "0 0 6px rgba(34,255,183,0.6)" }}
                />
                <span style={{ color: "#22FFB7" }}>Active</span>
              </span>
              {album.requires_passcode && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  style={{ background: "rgba(255,211,0,0.14)", color: "#FFD300", border: "1px solid rgba(255,211,0,0.45)" }}
                  data-testid={`locked-chip-${album.slug}`}
                  title="Guests need the passcode to view & upload"
                >
                  <Lock size={9} /> Locked
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.45)" }}>Items</div>
            <div className="text-lg font-semibold">{album.media_count || 0}</div>
          </div>
        </div>

        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2"
          style={{ background: "rgba(127,75,255,0.10)", border: "1px solid rgba(127,75,255,0.32)" }}
        >
          <Link2 size={14} style={{ color: "#7F4BFF" }} />
          <span className="text-sm truncate flex-1" style={{ color: "rgba(255,255,255,0.85)" }}>
            mEYEcam.com/<span className="font-semibold text-white">{album.slug}</span>
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="opacity-70 hover:opacity-100"
            aria-label="Copy invite"
            title="Copy invite — message + link + passcode"
            data-testid={`copy-link-${album.slug}`}
          >
            <Copy size={14} />
          </button>
        </div>

        {/* Passcode — visible to the host, with quick add/change */}
        <PasscodeRow album={album} onChanged={onChanged} />

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition"
            style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.45)", color: "#C9B6FF" }}
            data-testid={`view-album-${album.slug}`}
          >
            <ImageLucide size={14} />
            <span>View Album</span>
          </button>
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition"
            style={{ background: "rgba(255,211,0,0.14)", border: "1px solid rgba(255,211,0,0.45)", color: "#FFE373" }}
            data-testid={`upload-to-${album.slug}`}
            title="Add photos & videos to this event"
          >
            <Plus size={14} />
            <span>Add</span>
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition"
            style={{ background: "rgba(34,226,255,0.12)", border: "1px solid rgba(34,226,255,0.40)", color: "#A8F2FF" }}
            data-testid={`share-link-${album.slug}`}
            title="Share via your apps (or copy on desktop)"
          >
            <Share2 size={14} />
            <span>Share</span>
          </button>
          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 rounded-xl flex items-center justify-center transition"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              aria-label="More"
              data-testid={`more-${album.slug}`}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <>
                {/* Backdrop catches outside clicks/taps to close the menu */}
                <div
                  className="fixed inset-0"
                  style={{ zIndex: 40 }}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`menu-backdrop-${album.slug}`}
                />
              <div
                className="absolute right-0 bottom-full mb-2 rounded-2xl overflow-hidden"
                style={{
                  minWidth: 200,
                  zIndex: 50,
                  background: "#15131F",
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: "0 20px 60px -10px rgba(0,0,0,0.8)",
                }}
              >
                <button
                  onClick={() => { setMenuOpen(false); onOpen(); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5"
                >
                  Open album
                </button>
                <button
                  onClick={() => { setMenuOpen(false); pickCover(); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center gap-2"
                  data-testid={`menu-change-cover-${album.slug}`}
                >
                  <Camera size={14} />
                  <span>Change cover</span>
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onCopy(); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5"
                >
                  Copy invite
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center gap-2"
                  style={{ color: "#FF6B6B" }}
                  data-testid={`delete-event-${album.slug}`}
                >
                  <Trash2 size={14} />
                  <span>Delete event</span>
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────── Passcode row (per event) ─────────────────────────────────────
// Shows the passcode the host set (cached on this device), with show/hide,
// copy, and an inline "change" editor that saves a new code.
function PasscodeRow({ album, onChanged }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(() => album.host_passcode || getHostPasscodePlain(album.slug));

  // Keep in sync when the album list refreshes (e.g. after a change elsewhere)
  useEffect(() => {
    setCode(album.host_passcode || getHostPasscodePlain(album.slug));
  }, [album.host_passcode, album.slug]);

  const save = async () => {
    const p = val.trim();
    if (!p) return;
    const adding = !album.requires_passcode;
    setBusy(true);
    try {
      await Albums.patch(album.slug, { passcode: p });
      setHostPasscodePlain(album.slug, p);
      setCode(p);
      setEditing(false);
      setVal("");
      toast.success(adding ? "Passcode added — guests now need it to view" : "Passcode updated");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update passcode");
    } finally {
      setBusy(false);
    }
  };

  // No passcode yet → compact "Add passcode" affordance instead of the full row
  if (!album.requires_passcode && !editing) {
    return (
      <button
        type="button"
        onClick={() => { setEditing(true); setVal(""); }}
        className="inline-flex items-center gap-1.5 self-start text-[11px] font-medium px-2.5 py-1.5 rounded-lg"
        style={{ background: "rgba(255,211,0,0.07)", border: "1px dashed rgba(255,211,0,0.4)", color: "#FFE373" }}
        data-testid={`passcode-add-${album.slug}`}
        title="Require a passcode to view this album"
      >
        <Lock size={11} /> Add passcode
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-2 rounded-xl px-3 py-2"
      style={{ background: "rgba(255,211,0,0.08)", border: "1px solid rgba(255,211,0,0.35)" }}
      data-testid={`passcode-row-${album.slug}`}
    >
      <Lock size={14} color="#FFD300" />
      {!editing ? (
        <>
          <span
            className="text-sm font-semibold truncate flex-1"
            style={{ color: "#FFE373", letterSpacing: show || !code ? "normal" : "2px" }}
            data-testid={`passcode-value-${album.slug}`}
          >
            {code ? (show ? code : "•".repeat(Math.min(code.length, 10))) : "Passcode not saved on this device"}
          </span>
          {code && (
            <>
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="opacity-70 hover:opacity-100"
                aria-label={show ? "Hide passcode" : "Show passcode"}
                data-testid={`passcode-toggle-${album.slug}`}
              >
                {show ? <EyeOff size={14} color="#FFE373" /> : <Eye size={14} color="#FFE373" />}
              </button>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(code); toast.success("Passcode copied"); }}
                className="opacity-70 hover:opacity-100"
                aria-label="Copy passcode"
                data-testid={`passcode-copy-${album.slug}`}
              >
                <Copy size={14} color="#FFE373" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => { setEditing(true); setVal(code || ""); }}
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg"
            style={{ background: "rgba(255,211,0,0.14)", border: "1px solid rgba(255,211,0,0.45)", color: "#FFE373" }}
            data-testid={`passcode-change-${album.slug}`}
          >
            <Pencil size={11} /> Change
          </button>
        </>
      ) : (
        <>
          <input
            autoFocus
            type="text"
            className="meye-input flex-1"
            style={{ padding: "6px 10px", fontSize: 13 }}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="New passcode"
            maxLength={80}
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") { setEditing(false); setVal(""); }
            }}
            data-testid={`passcode-input-${album.slug}`}
          />
          <button
            type="button"
            onClick={save}
            disabled={busy || !val.trim()}
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(34,255,183,0.14)", border: "1px solid rgba(34,255,183,0.45)", opacity: !val.trim() ? 0.5 : 1 }}
            aria-label="Save passcode"
            data-testid={`passcode-save-${album.slug}`}
          >
            {busy ? <div className="meye-spinner" style={{ width: 12, height: 12 }} /> : <Check size={13} color="#22FFB7" />}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setVal(""); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5"
            aria-label="Cancel"
            data-testid={`passcode-cancel-${album.slug}`}
          >
            <XClose size={13} />
          </button>
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────── Joined events (from other hosts) ─────────────────────────────────────
// Albums from other hosts this signed-in user has visited. Auto-populated the
// moment they open someone else's event link while signed in.
function JoinedEvents({ albums, onChanged }) {
  const navigate = useNavigate();

  const remove = async (slug) => {
    try {
      await Joined.leave(slug);
      toast.success("Removed from your list");
      onChanged?.();
    } catch {
      toast.error("Couldn't remove");
    }
  };

  return (
    <section className="glass-card p-6" data-testid="joined-events-section">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(255,122,0,0.12)", border: "1px solid rgba(255,122,0,0.45)" }}
        >
          <Users size={20} color="#FF7A00" />
        </div>
        <div>
          <div className="text-lg font-semibold tracking-tight">Events You&apos;ve Joined</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            Events from other hosts you&apos;ve visited.
          </div>
        </div>
        <div className="ml-auto text-sm font-medium" style={{ color: "#FF7A00" }}>
          {albums.length}
        </div>
      </div>

      <div className="flex flex-col gap-3" data-testid="joined-events-list">
        {albums.map((a) => {
          const cfg = getIconConfig(a.icon);
          const Icon = cfg.Icon;
          return (
            <div
              key={a.id}
              className="flex items-center gap-3 rounded-2xl px-3.5 py-3"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              data-testid={`joined-row-${a.slug}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
                style={{ background: `${cfg.color}18`, border: `1px solid ${cfg.color}55` }}
              >
                {a.cover_media_id ? (
                  <img
                    src={Media.thumbUrl(a.cover_media_id)}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <Icon size={18} color={cfg.color} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => navigate(`/${a.slug}/gallery`)}
                  className="font-semibold text-sm truncate block text-left hover:text-cyan-300 transition"
                  data-testid={`joined-open-${a.slug}`}
                >
                  {a.name}
                </button>
                <div className="text-[11px] truncate" style={{ color: "rgba(255,255,255,0.5)" }}>
                  by {a.host_name || "another host"} · {a.media_count || 0} items
                  {a.requires_passcode && <Lock size={9} className="inline ml-1.5 -mt-0.5" color="#FFD300" />}
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/${a.slug}/upload`)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium shrink-0"
                style={{ background: "rgba(255,211,0,0.12)", border: "1px solid rgba(255,211,0,0.4)", color: "#FFE373" }}
                data-testid={`joined-upload-${a.slug}`}
              >
                <Plus size={11} /> Add
              </button>
              <button
                type="button"
                onClick={() => remove(a.slug)}
                className="w-7 h-7 rounded-lg flex items-center justify-center opacity-50 hover:opacity-100 shrink-0"
                aria-label="Remove from list"
                title="Remove from your dashboard"
                data-testid={`joined-remove-${a.slug}`}
              >
                <XClose size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ───────────────────────────────────── Right rail ─────────────────────────────────────

function RightRail({ stats }) {
  return (
    <aside className="flex flex-col gap-5" data-testid="right-rail">
      <AtAGlance stats={stats} />
      <RecentActivity items={stats?.recent_activity || []} />
      <QuickShare />
    </aside>
  );
}

function AtAGlance({ stats }) {
  const cards = [
    { label: "Total Events", value: stats?.total_events ?? 0, Icon: Calendar, color: "#9D4EDD" },
    { label: "Photos Uploaded", value: stats?.photos_uploaded ?? 0, Icon: ImageIcon, color: "#22E2FF" },
    { label: "Videos Uploaded", value: stats?.videos_uploaded ?? 0, Icon: Video, color: "#FF7A00" },
    { label: "Total Guests", value: stats?.total_guests ?? 0, Icon: Users, color: "#22FFB7" },
  ];
  return (
    <div
      className="glass-card p-5"
      style={{ borderColor: "rgba(34,226,255,0.30)", boxShadow: "0 0 0 1px rgba(34,226,255,0.12) inset" }}
      data-testid="at-a-glance"
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={16} color="#22E2FF" />
        <div className="text-base font-semibold tracking-tight">At a Glance</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cards.map(({ label, value, Icon, color }) => (
          <div
            key={label}
            className="rounded-2xl p-3.5"
            style={{ background: `${color}10`, border: `1px solid ${color}33` }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ background: `${color}22`, border: `1px solid ${color}55` }}
            >
              <Icon size={18} color={color} />
            </div>
            <div className="text-2xl font-bold leading-none">{value.toLocaleString()}</div>
            <div className="text-[11px] mt-1.5 tracking-wide" style={{ color: "rgba(255,255,255,0.55)" }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentActivity({ items }) {
  const navigate = useNavigate();
  return (
    <div className="glass-card p-5" data-testid="recent-activity">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} color="#FFD300" />
          <div className="text-base font-semibold tracking-tight">Recent Activity</div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/activity")}
          className="text-xs font-medium flex items-center gap-0.5 hover:opacity-80"
          style={{ color: "#22E2FF" }}
        >
          View all <ChevronRight size={12} />
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-center py-6" style={{ color: "rgba(255,255,255,0.5)" }}>
          No activity yet — share your event link to get going.
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.slice(0, 5).map((a, i) => (
            <ActivityItem key={i} activity={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityItem({ activity }) {
  const initials = (activity.actor_name || "?")
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const accent = pickColor(activity.actor_name || activity.album_slug || "x");

  const label = activity.type === "upload"
    ? activity.count > 1
      ? `uploaded ${activity.count} ${activity.kind === "video" ? "videos" : "photos"}`
      : `uploaded a ${activity.kind === "video" ? "video" : "photo"}`
    : activity.type === "comment"
      ? "left a comment"
      : "joined the event";

  const RightIcon = activity.type === "upload"
    ? (activity.kind === "video" ? Video : ImageIcon)
    : activity.type === "comment" ? MessageCircleMore : Users;
  const rightIconColor = activity.kind === "video" ? "#FF7A00" : activity.type === "comment" ? "#22E2FF" : "#22FFB7";

  return (
    <li className="flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
        style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}
      >
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">
          <span className="font-semibold">{activity.actor_name}</span>
          <span style={{ color: "rgba(255,255,255,0.7)" }}> {label}</span>
        </div>
        <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.45)" }}>
          {activity.album_name}
        </div>
      </div>
      <div className="text-xs shrink-0 text-right" style={{ color: "rgba(255,255,255,0.4)" }}>
        {timeAgo(activity.when)}
      </div>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${rightIconColor}1A`, border: `1px solid ${rightIconColor}44` }}
      >
        <RightIcon size={14} color={rightIconColor} />
      </div>
    </li>
  );
}

function pickColor(seed) {
  const palette = ["#9D4EDD", "#22E2FF", "#FF6B6B", "#FFD300", "#22FFB7", "#FF7A00", "#E63DDB", "#22A8FF"];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function QuickShare() {
  const url = window.location.origin;
  const message = "Check out mEYEcam — collect photos & videos from your event without logging in.";
  const share = (target) => {
    const enc = encodeURIComponent(`${message} ${url}`);
    const urls = {
      whatsapp: `https://wa.me/?text=${enc}`,
      messenger: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent("Try mEYEcam")}&body=${enc}`,
    };
    if (urls[target]) window.open(urls[target], "_blank", "noopener");
  };
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("mEYEcam link copied");
  };
  const tryNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "mEYEcam", text: message, url }); return; } catch {/* fall through */}
    }
    copy();
  };

  return (
    <div className="glass-card p-5" data-testid="quick-share">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Share2 size={16} color="#7F4BFF" />
          <div className="text-base font-semibold tracking-tight">Quick Share</div>
        </div>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition"
          style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.45)", color: "#C9B6FF" }}
          data-testid="quick-share-copy"
        >
          <Link2 size={12} />
          Copy Link
        </button>
      </div>
      <div className="text-xs mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
        Share mEYEcam with friends and family.
      </div>
      <div className="grid grid-cols-4 gap-2">
        <ShareCircle bg="#25D36622" border="#25D36666" onClick={() => share("whatsapp")} testid="share-whatsapp" label="WhatsApp">
          <WhatsAppIcon />
        </ShareCircle>
        <ShareCircle bg="#0084FF22" border="#0084FF66" onClick={() => share("messenger")} testid="share-messenger" label="Messenger">
          <MessengerIcon />
        </ShareCircle>
        <ShareCircle bg="#7F4BFF22" border="#7F4BFF66" onClick={() => share("email")} testid="share-email" label="Email">
          <Mail size={18} color="#C9B6FF" />
        </ShareCircle>
        <ShareCircle bg="rgba(255,255,255,0.05)" border="rgba(255,255,255,0.18)" onClick={tryNative} testid="share-more" label="More">
          <MoreHorizontal size={18} color="#fff" />
        </ShareCircle>
      </div>
    </div>
  );
}

function ShareCircle({ children, onClick, bg, border, testid, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
      data-testid={testid}
      title={label}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center transition group-hover:-translate-y-0.5"
        style={{ background: bg, border: `1px solid ${border}` }}
      >
        {children}
      </div>
      <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.55)" }}>{label}</span>
    </button>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
      <path d="M20.5 3.5A11.5 11.5 0 0 0 3.7 17.4L2.5 22l4.7-1.2A11.5 11.5 0 1 0 20.5 3.5Zm-8.5 18a9.5 9.5 0 0 1-4.8-1.3l-.3-.2-2.8.7.8-2.7-.2-.3a9.5 9.5 0 1 1 7.3 3.8Zm5.5-7.1c-.3-.2-1.8-.9-2-1s-.5-.2-.7.2-.8 1-.9 1.1-.3.2-.6 0a7.7 7.7 0 0 1-3.8-3.3c-.3-.5.3-.5.8-1.5a.5.5 0 0 0 0-.5c0-.2-.7-1.6-.9-2.1s-.5-.5-.7-.5h-.6a1 1 0 0 0-.8.3 3.2 3.2 0 0 0-1 2.4c0 1.4 1 2.7 1.2 2.9s2 3 4.9 4.2a16 16 0 0 0 1.6.6 4 4 0 0 0 1.8.1 3 3 0 0 0 2-1.4 2.4 2.4 0 0 0 .2-1.4c-.1-.1-.3-.2-.6-.3Z"/>
    </svg>
  );
}

function MessengerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#0084FF" aria-hidden="true">
      <path d="M12 2C6.4 2 2 6.2 2 11.5c0 2.9 1.4 5.5 3.6 7.2v3.8l3.3-1.8a10.7 10.7 0 0 0 3.1.4c5.6 0 10-4.2 10-9.6S17.6 2 12 2Zm1.1 12.7-2.6-2.7-5 2.7 5.4-5.7 2.6 2.6 4.9-2.6-5.3 5.7Z"/>
    </svg>
  );
}

// ───────────────────────────────────── Logged-out hero ─────────────────────────────────────

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#EA4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
    <path fill="#34A853" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
    <path fill="#FBBC05" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
    <path fill="#4285F4" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z"/>
  </svg>
);

function LoggedOutHero({ loading }) {
  const { signIn } = useAuth();
  return (
    <>
      <div
        className="hidden sm:flex justify-center"
        style={{ position: "fixed", left: 0, right: 0, bottom: "max(48px, calc(env(safe-area-inset-bottom) + 24px))", zIndex: 5 }}
      >
        <button
          onClick={signIn}
          disabled={loading}
          className="inline-flex items-center gap-3 rounded-full bg-white text-black font-semibold px-7 py-3 text-base shadow-2xl transition hover:bg-white/90 disabled:opacity-60"
          data-testid="signin-google-bottom"
          style={{ boxShadow: "0 24px 60px -16px rgba(34, 226, 255, 0.45), 0 18px 50px -20px rgba(127, 75, 255, 0.55)" }}
        >
          <GoogleG />
          <span>Sign in with Google</span>
        </button>
      </div>
      <div className="flex sm:hidden justify-center mt-12">
        <button
          onClick={signIn}
          disabled={loading}
          className="inline-flex items-center gap-3 rounded-full bg-white text-black font-semibold px-6 py-3 text-base shadow-2xl transition hover:bg-white/90 disabled:opacity-60"
          data-testid="signin-google-mobile"
          style={{ boxShadow: "0 24px 60px -16px rgba(34, 226, 255, 0.45), 0 18px 50px -20px rgba(127, 75, 255, 0.55)" }}
        >
          <GoogleG />
          <span>Sign in with Google</span>
        </button>
      </div>
    </>
  );
}
